import { useCallback } from 'react';
import { format, toDate } from 'date-fns';
import { JiraIssueAri } from '@atlassian/ari/jira';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	AdditionalFieldValue,
	CreatedIssueWithIssueData,
} from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { GIC_LIFECYCLE_EVENTS } from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/constants.tsx';
import { useGICLifeCycleEvents } from '@atlassian/jira-issue-create-extensibility/src/common/utils/lifecycle-events/main.tsx';
import { JIRA_ISSUE_TABLE_TRIGGER } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/constants.tsx';
import { useOpenIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/main.tsx';
import type { GroupContextField } from '@atlassian/jira-issue-table-grouping-issue-create-utils/src/types.tsx';
import {
	convertFieldToIssueCreateInput,
	convertFieldToStatusGICInput,
	convertFieldToGICAdditionalFieldsPayload,
} from '@atlassian/jira-issue-table-grouping-issue-create-utils/src/index.tsx';
import { STATUS_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import type { ContextualFields, IssueToCreate } from '../../common/types.tsx';

type OnFallbackToGIC = (args: {
	issueToCreate: IssueToCreate;
	contextualFields: ContextualFields;
	groupContextFieldInput?: GroupContextField;
}) => Promise<string | null>;

type GICAdditionalFields = {
	[key: string]: AdditionalFieldValue;
};

const issueIdFromAri = (ari: string): string => JiraIssueAri.parse(ari).issueId;

const toGICAdditionalFields = (
	{ rankFieldId, rankBeforeIssueId, rankAfterIssueId }: ContextualFields,
	groupContextFieldInput?: GroupContextField,
): GICAdditionalFields => {
	const additionalFields: GICAdditionalFields = {};
	if (rankFieldId && (rankBeforeIssueId || rankAfterIssueId)) {
		if (fg('jsc_list_reranking_gate')) {
			additionalFields[rankFieldId] = {
				...(rankBeforeIssueId ? { rankBeforeIssue: issueIdFromAri(rankBeforeIssueId) } : {}),
				...(rankAfterIssueId ? { rankAfterIssue: issueIdFromAri(rankAfterIssueId) } : {}),
			};
		} else {
			additionalFields[rankFieldId] = {
				...(rankBeforeIssueId ? { rankBeforeIssue: rankBeforeIssueId } : {}),
				...(rankAfterIssueId ? { rankAfterIssue: rankAfterIssueId } : {}),
			};
		}
	}

	if (groupContextFieldInput) {
		return {
			...additionalFields,
			...convertFieldToGICAdditionalFieldsPayload(groupContextFieldInput),
		};
	}

	return additionalFields;
};

/**
 * Open the global issue create modal prefilled with field values provided during inline issue create. This returns a
 * promise that will resolve with the issue key of the newly created issue, or `null` if the modal was dismissed.
 */

export const useFallbackToGIC = (): OnFallbackToGIC => {
	const openIssueCreateModal = useOpenIssueCreateModal();
	const { subscribe } = useGICLifeCycleEvents();
	return useCallback<OnFallbackToGIC>(
		({ issueToCreate, contextualFields, groupContextFieldInput }) => {
			const {
				project: { projectId },
				issueType: { issueTypeId },
				summary,
				assignee,
				dueDate,
			} = issueToCreate;

			const payload = {
				project: {
					projectId,
				},
				issueType: {
					issueTypeId,
				},
				...(contextualFields.parentIssueAri
					? {
							parentId: {
								parentId: issueIdFromAri(contextualFields.parentIssueAri),
							},
						}
					: {}),
				...(groupContextFieldInput?.fieldType === STATUS_TYPE
					? convertFieldToStatusGICInput(groupContextFieldInput)
					: {}),

				defaultValues: {
					singleLineTextFields: [
						{
							fieldId: 'summary',
							text: summary,
						},
					],
					...(assignee &&
						fg('jira_render_pic_in_nin') && {
							singleSelectUserPickerFields: [
								{ fieldId: 'assignee', user: { accountId: assignee.accountId } },
							],
						}),
					...(dueDate &&
						fg('jira_render_pic_in_nin') && {
							datePickerFields: [
								{
									fieldId: 'duedate',
									date: { formattedDate: format(toDate(dueDate), 'yyyy-MM-dd') },
								},
							],
						}),
					...(contextualFields.parentIssueAri
						? {
								parentField: {
									issueId: issueIdFromAri(contextualFields.parentIssueAri),
								},
							}
						: {}),
					...(groupContextFieldInput ? convertFieldToIssueCreateInput(groupContextFieldInput) : {}),
				},
			};

			return new Promise((resolve) => {
				const unsubscribe = subscribe(
					GIC_LIFECYCLE_EVENTS.MODAL_KEEP_EDITING_EXISTING_ISSUE,
					() => {
						// the user cancelled editing
						onClose(null);
					},
				);

				// resolve the promise once the GIC has been closed or cancelled
				const onClose = (data: CreatedIssueWithIssueData[] | null | undefined) => {
					const result = data?.[0]?.issueKey ?? null;
					resolve(result);
					unsubscribe();
				};

				const isSubTaskCreationOpen = !!(issueToCreate.issueType?.hierarchy?.level === -1);

				openIssueCreateModal({
					payload,
					context: {
						projectId,
						submitApiData: {
							additionalFields: toGICAdditionalFields(contextualFields, groupContextFieldInput),
						},
					},
					onClose,
					isSubTaskCreationOpen,
					triggerPointKey: JIRA_ISSUE_TABLE_TRIGGER,
					disableProjectDropdown: true,
					disableIssueTypeDropdown: false,
					disableCreationSuccessFlag: true,
					minimizableModalView: 'mini',
				});
			});
		},
		[openIssueCreateModal, subscribe],
	);
};
